import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Edit2, Trash2, Plus } from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, query, orderBy, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { useEditMode } from '../hooks/useEditMode';

interface Event {
  id: string;
  title: string;
  description?: string;
  date: string;
  type: string;
}

const Agenda = () => {
  const { isEditMode } = useEditMode();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(2026);

  useEffect(() => {
    fetchEvents();
  }, [selectedYear]);

  const fetchEvents = async () => {
    setIsLoading(true);
    try {
      const eventsRef = collection(db, 'events');
      const q = query(eventsRef, orderBy('date', 'asc'));
      const querySnapshot = await getDocs(q);
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Event));

      // Filter events by selected year
      const filteredEvents = eventsData.filter(event => {
        const eventYear = new Date(event.date).getFullYear();
        return eventYear === selectedYear;
      });

      setEvents(filteredEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      setError('Impossible de charger les événements. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (event: Event) => {
    try {
      if (event.id) {
        const eventRef = doc(db, 'events', event.id);
        await updateDoc(eventRef, {
          title: event.title,
          description: event.description,
          date: event.date,
          type: event.type,
          updatedAt: new Date()
        });
      } else {
        await addDoc(collection(db, 'events'), {
          title: event.title,
          description: event.description,
          date: event.date,
          type: event.type,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }

      fetchEvents();
      setIsEditing(false);
      setCurrentEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'events', id));
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const eventTypes = {
    'comite': 'Comité',
    'fondue': 'Fondue traditionnelle',
    'assemblee': 'Assemblée générale',
    'learning': 'Learning Lunch'
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-serif font-bold text-dark mb-12">
          Agenda {selectedYear}
        </h1>
        <div className="flex justify-center items-center min-h-[200px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dark"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
        <div className="flex items-center gap-4">
          <h1 className="text-4xl font-serif font-bold text-dark">
            Agenda {selectedYear}
          </h1>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedYear(2025)}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedYear === 2025
                  ? 'bg-dark text-beige'
                  : 'bg-gray-200 text-dark hover:bg-gray-300'
              }`}
            >
              2025
            </button>
            <button
              onClick={() => setSelectedYear(2026)}
              className={`px-4 py-2 rounded-md transition-colors ${
                selectedYear === 2026
                  ? 'bg-dark text-beige'
                  : 'bg-gray-200 text-dark hover:bg-gray-300'
              }`}
            >
              2026
            </button>
          </div>
        </div>
        {isEditMode && !isEditing && (
          <button
            onClick={() => {
              setIsEditing(true);
              setCurrentEvent(null);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-dark text-beige rounded-md hover:bg-dark/90 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Ajouter un événement
          </button>
        )}
      </div>

      {isEditing && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">
            {currentEvent ? 'Modifier l\'événement' : 'Nouvel événement'}
          </h2>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              handleSave({
                id: currentEvent?.id || '',
                title: formData.get('title') as string,
                description: formData.get('description') as string,
                date: formData.get('date') as string,
                type: formData.get('type') as string,
              });
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700">Type</label>
              <select
                name="type"
                defaultValue={currentEvent?.type || ''}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                required
              >
                {Object.entries(eventTypes).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Titre</label>
              <input
                type="text"
                name="title"
                defaultValue={currentEvent?.title || ''}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                name="description"
                defaultValue={currentEvent?.description || ''}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <input
                type="datetime-local"
                name="date"
                defaultValue={currentEvent?.date || `${selectedYear}-01-01T12:00`}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                required
              />
            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setCurrentEvent(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-dark text-beige rounded-md hover:bg-dark/90"
              >
                Enregistrer
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-6">
        {events.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Aucun événement n'est prévu pour le moment.
          </div>
        ) : (
          events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow-lg p-6 flex items-start justify-between hover:transform hover:scale-[1.02] transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div className="bg-dark text-beige p-3 rounded-lg">
                  <Calendar className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-dark mb-2">{event.title}</h3>
                  <p className="text-dark/60 mb-2">
                    {format(new Date(event.date), 'PPP à HH:mm', { locale: fr })}
                  </p>
                  {event.description && (
                    <p className="text-dark/80">{event.description}</p>
                  )}
                  <p className="text-dark/60 mt-2 text-sm">
                    Type: {eventTypes[event.type as keyof typeof eventTypes]}
                  </p>
                </div>
              </div>
              {isEditMode && (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setCurrentEvent(event);
                      setIsEditing(true);
                    }}
                    className="p-2 text-dark/60 hover:text-dark hover:bg-gray-100 rounded-md"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    className="p-2 text-red-600/60 hover:text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Agenda;